import { useCallback, useEffect, useMemo } from 'react';

import { FormikValues } from 'formik/dist/types';

import { useGetTrainingActivityTypes } from '../redux/api/trainingsAPI';
import { ITrainingActivityType } from '../types/interfaces/training-activity-type';

function resolveSelectedActivityTypeIdFromCatalog(
  activityTypeId: string | null | undefined,
  activityTypes: ITrainingActivityType[],
): string | null {
  if (!activityTypeId) {
    return null;
  }

  return activityTypes.some((type) => type.id === activityTypeId)
    ? activityTypeId
    : null;
}

export function useTrainingActivityTypePicker() {
  const {
    data: activityTypes = [],
    isLoading,
    isError,
  } = useGetTrainingActivityTypes();

  const defaultActivityTypeId = useMemo(() => {
    if (!activityTypes.length) {
      return null;
    }

    const defaultType = activityTypes.find((type) => type.isDefault);
    return defaultType?.id ?? activityTypes[0]?.id ?? null;
  }, [activityTypes]);

  const isPickerDisabled = isLoading || isError || activityTypes.length === 0;

  const resolveActivityTypeById = useCallback(
    (
      activityTypeId: string | null | undefined,
    ): ITrainingActivityType | null => {
      if (!activityTypeId) {
        return null;
      }

      return activityTypes.find((type) => type.id === activityTypeId) ?? null;
    },
    [activityTypes],
  );

  const resolveSelectedActivityTypeId = useCallback(
    (activityTypeId: string | null | undefined): string | null =>
      resolveSelectedActivityTypeIdFromCatalog(activityTypeId, activityTypes),
    [activityTypes],
  );

  return {
    activityTypes,
    isLoading,
    isError,
    isPickerDisabled,
    defaultActivityTypeId,
    resolveActivityTypeById,
    resolveSelectedActivityTypeId,
  };
}

export function isActivityTypeSubmitBlocked(
  isLoading: boolean,
  activityTypes: ITrainingActivityType[],
  activityTypeId: string | null | undefined,
  defaultActivityTypeId: string | null,
  isError = false,
): boolean {
  if (isLoading || isError) {
    return true;
  }

  if (activityTypes.length > 0) {
    return (
      resolveActivityTypeIdForSubmit(
        activityTypeId,
        defaultActivityTypeId,
        activityTypes,
      ) == null
    );
  }

  return false;
}

export function resolveActivityTypeIdForSubmit(
  activityTypeId: string | null | undefined,
  defaultActivityTypeId: string | null,
  activityTypes: ITrainingActivityType[],
): string | null {
  const resolvedSelection = resolveSelectedActivityTypeIdFromCatalog(
    activityTypeId,
    activityTypes,
  );

  if (resolvedSelection != null) {
    return resolvedSelection;
  }

  if (activityTypes.length > 0 && defaultActivityTypeId) {
    return defaultActivityTypeId;
  }

  return null;
}

export function useTrainingActivityTypePreselect(
  formik: FormikValues,
  fieldName = 'activityTypeId',
) {
  const { defaultActivityTypeId, resolveSelectedActivityTypeId } =
    useTrainingActivityTypePicker();
  const currentActivityTypeId = formik.values[fieldName];

  useEffect(() => {
    if (formik.dirty) {
      return;
    }

    const isValidSelection =
      resolveSelectedActivityTypeId(currentActivityTypeId) != null;

    if (currentActivityTypeId != null && isValidSelection) {
      return;
    }

    if (defaultActivityTypeId) {
      if (currentActivityTypeId !== defaultActivityTypeId) {
        formik.setFieldValue(fieldName, defaultActivityTypeId);
      }
      return;
    }

    if (currentActivityTypeId != null) {
      formik.setFieldValue(fieldName, null);
    }
  }, [
    currentActivityTypeId,
    defaultActivityTypeId,
    fieldName,
    formik.dirty,
    formik.setFieldValue,
    resolveSelectedActivityTypeId,
  ]);
}
